import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { api, refreshAccessToken, setAccessToken, setSessionExpiredHandler } from '../api/client';
import type { Me } from '../api/types';

interface AuthState {
  me: Me | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  can: (permission: string) => boolean;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    setMe(await api.get<Me>('/admin/auth/me'));
  }, []);

  // Khôi phục phiên khi tải lại trang: dùng cookie refresh httpOnly.
  useEffect(() => {
    setSessionExpiredHandler(() => {
      setAccessToken(null);
      setMe(null);
    });
    void (async () => {
      try {
        if (await refreshAccessToken()) await loadMe();
      } finally {
        setLoading(false);
      }
    })();
  }, [loadMe]);

  const value = useMemo<AuthState>(
    () => ({
      me,
      loading,
      async login(email, password) {
        const r = await api.post<{ access_token: string }>('/admin/auth/login', {
          email,
          password,
        });
        setAccessToken(r.access_token);
        await loadMe();
      },
      async logout() {
        try {
          await api.post('/admin/auth/logout');
        } finally {
          setAccessToken(null);
          setMe(null);
        }
      },
      can: (p) => Boolean(me?.permissions.includes(p)),
    }),
    [me, loading, loadMe],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth ngoài AuthProvider');
  return v;
}
